import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Separator } from "../ui/separator";
import { IconSave } from "./icons";
import { isAccountPrimaryRuntime } from "../../lib/runtime";

export function TopTitleBar({
  bookTitle,
  onBookTitleChange,
  filePath,
  saving = false,
  syncHint,
  onSave,
  onOpenFeedback,
  onOpen,
  onNew,
  canUseLibraryCopy,
  onUseLibraryCopy,
  canOpenFromCloud,
  onOpenFromCloud,
}: {
  bookTitle: string;
  onBookTitleChange: (title: string) => void;
  filePath: string | null;
  saving?: boolean;
  syncHint?: string;
  onSave: () => void;
  onOpenFeedback: () => void;
  onOpen?: () => void;
  onNew?: () => void;
  canUseLibraryCopy?: boolean;
  onUseLibraryCopy?: () => void;
  canOpenFromCloud?: boolean;
  onOpenFromCloud?: () => void;
}) {
  const accountPrimary = isAccountPrimaryRuntime();
  const rawName = filePath ? filePath.split(/[\\/]/).pop() ?? filePath : null;
  const looksLikeBookId = Boolean(rawName && /^book[-_]/i.test(rawName));
  const displayPath = !rawName
    ? "Unsaved project"
    : looksLikeBookId
      ? "On this computer"
      : rawName;

  return (
    <header className="flex shrink-0 items-center justify-between gap-4 border-b border-border bg-card px-3.5 py-2">
      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <Input
          aria-label="Book title"
          className="-ml-2 h-auto max-w-lg border-transparent bg-transparent px-2 py-1 text-lg font-bold tracking-tight shadow-none hover:border-border/70 focus-visible:bg-card focus-visible:ring-0"
          placeholder="Untitled book"
          value={bookTitle}
          onChange={(event) => onBookTitleChange(event.target.value)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate pl-px text-xs text-muted-foreground" title={accountPrimary ? undefined : filePath ?? undefined}>
            {accountPrimary ? syncHint || (saving ? "Saving" : "Saved") : displayPath}
          </span>
        </div>
      </div>
      <Separator orientation="vertical" className="hidden h-8 sm:block" />
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        {accountPrimary ? (
          <>
            {onOpen ? (
              <Button variant="outline" size="sm" className="h-8 shrink-0 px-2.5 text-xs font-medium" onClick={onOpen}>
                Open
              </Button>
            ) : null}
            {onNew ? (
              <Button variant="outline" size="sm" className="h-8 shrink-0 px-2.5 text-xs font-medium" onClick={onNew}>
                New
              </Button>
            ) : null}
          </>
        ) : (
          <>
            {canOpenFromCloud && onOpenFromCloud ? (
              <Button variant="outline" size="sm" className="h-8 shrink-0 px-2.5 text-xs font-medium" onClick={onOpenFromCloud}>
                Open from cloud
              </Button>
            ) : null}
            {canUseLibraryCopy && onUseLibraryCopy ? (
              <Button variant="outline" size="sm" className="h-8 shrink-0 px-2.5 text-xs font-medium" onClick={onUseLibraryCopy}>
                Use library copy
              </Button>
            ) : null}
          </>
        )}
        <Button
          variant="secondary"
          size="sm"
          className="h-8 shrink-0 gap-1.5 px-2.5 text-xs font-medium"
          onClick={() => onSave()}
          disabled={saving}
          title="Save (⌘S / Ctrl+S)"
        >
          <IconSave size={16} />
          Save
        </Button>
        <Button variant="ghost" size="sm" className="h-8 shrink-0 text-xs font-medium" onClick={onOpenFeedback}>
          Feedback
        </Button>
      </div>
    </header>
  );
}
